import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { NEWSLETTER_KEY } from "@/lib/startHere";

/**
 * Minimal email opt-in used at the bottom of blog posts.
 *
 * Deliberately low-friction: no name field, no double opt-in. This is a
 * lead magnet, not a trial signup — nurtured separately into paid via
 * Resend broadcasts.
 */
export default function NewsletterCapture({ source = "blog-post", postSlug = null }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setBusy(true);
      const res = await subscribeNewsletter({
        email: email.trim(),
        source,
        post_slug: postSlug,
      });
      setSubscribed(true);
      setMessage(res.message || "You're on the list.");
      try {
        window.localStorage.setItem(NEWSLETTER_KEY, "1");
      } catch {
        /* storage disabled */
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went sideways. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside
      className="mt-16 border border-obsidian-600 bg-obsidian-800/30 p-8 sm:p-10"
      aria-label="Newsletter signup"
      data-testid="newsletter-capture"
    >
      <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-3">
        The field notes
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 leading-snug mb-3 max-w-xl">
        Get new essays every fortnight.
      </h2>
      <p className="font-serif italic text-bone-300 text-[1rem] leading-relaxed max-w-xl mb-6">
        Quiet drops on shadow work, voice journaling, and the practical mechanics
        of self-inquiry. No noise. Unsubscribe anytime.
      </p>

      {subscribed ? (
        <div
          className="font-sans text-sm text-bone-100 border border-terracotta bg-terracotta/10 px-4 py-3 max-w-xl"
          data-testid="newsletter-success"
          role="status"
        >
          {message}
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="flex flex-col sm:flex-row gap-2 max-w-xl"
          data-testid="newsletter-form"
        >
          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <div className="relative flex-1">
            <Mail
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bone-400"
              aria-hidden="true"
            />
            <input
              id="newsletter-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@quiet-inbox.com"
              data-testid="newsletter-email-input"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 pl-9 pr-3 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50"
              style={{ fontSize: "16px" }}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="newsletter-submit"
            className="bg-terracotta hover:bg-terracotta-deep disabled:opacity-60 text-obsidian-900 font-sans uppercase tracking-[0.24em] text-xs font-semibold px-6 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
          </button>
        </form>
      )}
    </aside>
  );
}
